import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Car, ArrowRight, Plus, Phone, X } from "lucide-react";
import { motion } from "framer-motion";
import { useContent, Slashed } from "../lib/content";

function CarpoolHeader() {
  const { c } = useContent();
  return (
    <div>
      <div className="label-mono text-neon mb-2">{c.carpool_label}</div>
      <h1 className="font-display text-4xl lg:text-6xl whitespace-pre-line"><Slashed text={c.carpool_title} className="text-alert"/></h1>
      <p className="mt-4 text-neutral-400 max-w-xl">{c.carpool_subtitle}</p>
    </div>
  );
}

export default function Carpool() {
  const [rides, setRides] = useState([]);
  const [filter, setFilter] = useState("all");
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const load = useCallback(async () => {
    const q = filter === "all" ? "" : `?kind=${filter}`;
    const [r, e] = await Promise.all([api.get(`/carpool${q}`), api.get("/events")]);
    setRides(r.data); setEvents(e.data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16">
      <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4 mb-10">
        <CarpoolHeader/>
        <button onClick={()=> user ? setShowForm(true) : nav("/login")} className="btn-primary flex items-center gap-2" data-testid="create-ride-btn">
          <Plus className="w-4 h-4"/> PUBBLICA
        </button>
      </div>

      <div className="flex gap-2 mb-6 font-mono text-xs tracking-widest">
        {[["all","TUTTI"],["offer","OFFRO"],["request","CERCO"]].map(([k,l])=>(
          <button key={k} onClick={()=>setFilter(k)}
            className={`px-4 py-2 border transition ${filter===k?"border-neon text-neon":"border-neutral-800 text-neutral-400 hover:text-white"}`}
            data-testid={`filter-${k}`}>{l}</button>
        ))}
      </div>

      <div className="space-y-3">
        {rides.length === 0 && (
          <div className="border border-neutral-800 p-12 text-center">
            <Car className="w-8 h-8 mx-auto mb-3 text-neutral-700"/>
            <div className="label-mono text-neutral-500">Nessun passaggio al momento.</div>
          </div>
        )}
        {rides.map((r, i) => (
          <motion.div key={r.id} initial={{opacity:0,x:-10}} animate={{opacity:1,x:0}} transition={{delay:i*0.04}}
            className="card-dark p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`ride-${r.id}`}>
            <div className={`px-3 py-1 label-mono text-[10px] border ${r.kind==="offer"?"border-neon text-neon":"border-alert text-alert"} w-fit`}>
              {r.kind==="offer"?"OFFRO":"CERCO"}
            </div>
            <div className="flex-1 flex items-center gap-3 font-display text-xl flex-wrap">
              <span>{r.from_city}</span>
              <ArrowRight className="w-4 h-4 text-neutral-500"/>
              <span className="text-neon">{r.to_venue}</span>
            </div>
            <div className="font-mono text-xs text-neutral-400 tracking-widest">
              {new Date(r.date).toLocaleDateString("it-IT",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}
            </div>
            <div className="flex items-center gap-4">
              <div className="font-mono text-xs text-neutral-400">POSTI <span className="text-white">{r.seats}</span></div>
              <div className="font-display text-lg text-neon">€{r.price}</div>
              <a href={`tel:${r.contact}`} className="btn-ghost !py-2 !px-3 text-xs flex items-center gap-1" data-testid={`contact-${r.id}`}>
                <Phone className="w-3 h-3"/> {r.contact}
              </a>
            </div>
          </motion.div>
        ))}
      </div>

      {showForm && <CarpoolForm events={events} onClose={()=>setShowForm(false)} onCreated={()=>{setShowForm(false); load();}}/>}
    </div>
  );
}

function CarpoolForm({ events, onClose, onCreated }) {
  const [form, setForm] = useState({ kind: "offer", event_id: events[0]?.id || "", from_city: "", to_venue: "", date: "", seats: 3, price: 10, contact: "", note: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/carpool", {
        ...form,
        seats: Number(form.seats), price: Number(form.price),
        date: new Date(form.date).toISOString()
      });
      toast.success("Passaggio pubblicato!");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-neutral-950 border border-neutral-800 w-full max-w-md p-8 relative" onClick={e=>e.stopPropagation()} data-testid="carpool-modal">
        <button className="absolute top-4 right-4 text-neutral-500 hover:text-white" onClick={onClose}><X/></button>
        <div className="label-mono text-neon mb-2">NUOVO PASSAGGIO</div>
        <form onSubmit={submit} className="space-y-5 mt-6">
          <div className="grid grid-cols-2 gap-2">
            {["offer","request"].map(k => (
              <button type="button" key={k} onClick={()=>setForm({...form,kind:k})}
                className={`py-3 border font-mono text-xs tracking-widest ${form.kind===k?"border-neon text-neon":"border-neutral-800 text-neutral-500"}`}
                data-testid={`kind-${k}`}>{k==="offer"?"OFFRO":"CERCO"}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><div className="label-mono mb-1">DA</div><input required value={form.from_city} onChange={e=>setForm({...form,from_city:e.target.value})} className="input-dark" data-testid="ride-from"/></div>
            <div><div className="label-mono mb-1">A (locale)</div><input required value={form.to_venue} onChange={e=>setForm({...form,to_venue:e.target.value})} className="input-dark" data-testid="ride-to"/></div>
          </div>
          <div>
            <div className="label-mono mb-1">DATA & ORA</div>
            <input required type="datetime-local" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} className="input-dark" data-testid="ride-date"/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><div className="label-mono mb-1">POSTI</div><input required type="number" min="1" max="8" value={form.seats} onChange={e=>setForm({...form,seats:e.target.value})} className="input-dark" data-testid="ride-seats"/></div>
            <div><div className="label-mono mb-1">PREZZO €</div><input required type="number" min="0" value={form.price} onChange={e=>setForm({...form,price:e.target.value})} className="input-dark" data-testid="ride-price"/></div>
          </div>
          <div>
            <div className="label-mono mb-1">CONTATTO (tel/instagram)</div>
            <input required value={form.contact} onChange={e=>setForm({...form,contact:e.target.value})} className="input-dark" data-testid="ride-contact"/>
          </div>
          <button disabled={loading} className="btn-primary w-full" data-testid="ride-submit">{loading?"...":"PUBBLICA"}</button>
        </form>
      </div>
    </div>
  );
}
